import {adminUrlBuilder} from "@app/api/utils";
import {
  ICalendarData,
  ICalendarEvent,
  IClientManualAppointment, IFullCalendarEvent,
  IManualAppointment,
  IStatsData,
  TAppointmentItem
} from '@torim-app/types';

export class AppointmentsApi {
  static readonly path = 'appointments';

  static async fetchCalendarData(providerIds: string | string[], fromDate?: string, toDate?: string): Promise<ICalendarData> {
    const providerId = Array.isArray(providerIds) ? providerIds.join(',') : providerIds;
    const queryParams: Record<string, string> = {providerId};
    if (fromDate) queryParams.from = fromDate;
    if (toDate) queryParams.to = toDate;

    const url = adminUrlBuilder(queryParams, AppointmentsApi.path);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw "Failed to get appointments";

    return await response.json();
  }

  // Every appointment still waiting for the business to approve it (today onwards),
  // regardless of which month the calendar currently has loaded.
  static async fetchPendingAppointments(providerIds: string | string[]): Promise<ICalendarEvent[]> {
    const providerId = Array.isArray(providerIds) ? providerIds.join(',') : providerIds;
    const url = adminUrlBuilder(providerId ? {providerId} : {}, `${AppointmentsApi.path}/pending`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw "Failed to get pending appointments";

    return await response.json();
  }

  // Manual calendar events whose title matches `term` (home search).
  static async searchManualEvents(term: string): Promise<ICalendarEvent[]> {
    const url = adminUrlBuilder({q: term}, `${AppointmentsApi.path}/manual-events/search`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw "Failed to search manual events";

    return await response.json();
  }

  static async fetchAppointmentData(apid: string): Promise<IFullCalendarEvent> {
    const url = adminUrlBuilder({}, AppointmentsApi.path);
    const response = await fetch(`${url}/${apid}`, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw "Failed to get appointment data";

    return await response.json();
  }

  static async getCalendarStats(providerId: string): Promise<IStatsData> {
    const url = adminUrlBuilder({providerId}, `${AppointmentsApi.path}/stats`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw "Failed to get appointments";

    return await response.json();
  }

  static async addManualAppointment(
    body: IManualAppointment
  ): Promise<void> {
    const url = adminUrlBuilder({}, AppointmentsApi.path);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok)
      throw `Failed to add manual appointment`;
  }

  static async cancelAppointment(
    args: { apid?: string, date?: string, providerId?: string, skipNotification?: boolean, reason?: string }
  ): Promise<void> {
    const url = adminUrlBuilder(args, AppointmentsApi.path);
    const response = await fetch(url, {
      method: 'delete',
      headers: {
        'content-type': 'application/json'
      },
    });
    if (!response.ok)
      throw `Failed to cancel appointment`;
  }

  static async approveAppointment(
    apid: string
  ): Promise<void> {
    const url = adminUrlBuilder({apid}, AppointmentsApi.path);
    const response = await fetch(url, {
      method: 'put',
    });
    if (!response.ok)
      throw `Failed to approve appointment`;
  }

  static async addManualEvent(
    body: IClientManualAppointment
  ): Promise<void> {
    const url = adminUrlBuilder({}, `${AppointmentsApi.path}/event`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      if (response.status === 409)
        throw "Slot is taken"
      if (response.status === 422)
        throw "Reached Limit"
      throw "Failed to create new manual event";
    }
  }

  static async getEventAlternativeSlots(
    apid: string,
    selectedDate: string,
  ): Promise<{ available: number[]; adminAvailable: number[] }> {
    const url = adminUrlBuilder({apid, selectedDate}, `${AppointmentsApi.path}/event/slots/v2`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw "Failed to get edit visit new slots";

    return await response.json();
  }

  /**
   * @param force Owner override — place the visit at exactly this time even if the slot
   *   is taken, sits outside working hours, or doesn't line up with the booking grid.
   */
  static async editAppointment(
    apid: string,
    origin: 'drag' | 'select',
    selectedDate?: string,
    selectedHour?: number,
    duration?: number,
    stepDurations?: number[],
    force?: boolean
  ): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    const url = adminUrlBuilder({apid, selectedDate, selectedHour, origin, duration, ...(force ? {force: true} : {})}, `${AppointmentsApi.path}/event`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: stepDurations ? JSON.stringify({ stepDurations }) : undefined
    });
    if (!response.ok) {
      if (response.status === 409)
        throw "Slot is taken"
      throw "Failed to edit event";
    }
  }

  static async editManualEventTimes(
    apid: string,
    timeFrom: number,
    timeTo: number,
    date?: string,
    title?: string,
  ): Promise<void> {
    const url = adminUrlBuilder({ apid }, `${AppointmentsApi.path}/event/manual-times`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({ timeFrom, timeTo, date, title })
    });
    if (!response.ok)
      throw 'Failed to edit manual event times';
  }

  static async markAppointmentAsCompleted(
    apid: string,
  ): Promise<void> {
    const url = adminUrlBuilder({apid}, `${AppointmentsApi.path}/event/complete`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw "Failed to mark event as completed";
  }

  static async markAppointmentAsNoShow(
    apid: string,
  ): Promise<void> {
    const url = adminUrlBuilder({apid}, `${AppointmentsApi.path}/event/no-show`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw "Failed to mark event as no-show";
  }


  static async updateAppointmentServices(
    apid: string,
    serviceIds: string[]
  ): Promise<void> {
    const url = adminUrlBuilder({apid}, `${AppointmentsApi.path}/event/services`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({ serviceIds })
    });
    if (!response.ok)
      throw 'Failed to update appointment services';
  }

  static async updateCartItemsInAppointment(
    apid: string,
    items: Array<TAppointmentItem>,
  ) {
    const url = adminUrlBuilder({apid}, `${AppointmentsApi.path}/update-cart`);
    try {
      const response = await fetch(url, {
        method: 'put',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify(items)
      });

      if (!response.ok)
        throw 'Failed to updateCartItemsInAppointment';

    } catch (e) {
      console.error(e)
      throw e;
    }
  }
}
